/**
 * TransactionEditDialogStandalone
 *
 * Renders the same TransactionDialog used on Activity, for use on other pages
 * (e.g. CategoryGroupDetail / Analytics) so editing stays on the same page.
 */

import { ReactElement, useEffect, useState, useCallback, useRef } from "react";
import { Account, TransactionCategory, TransactionCategoryGroup, TransactionCustomField, Currency } from "../../../types";
import { TransactionWithDate } from "../../../utils/transactionProcessing";
import TransactionDialog from "./TransactionDialog";
import { useAccountHelpers } from "../hooks/useAccountHelpers";
import { useCurrencyHelpers } from "../hooks/useCurrencyHelpers";
import { useDialogHandlers } from "../hooks/useDialogHandlers";
import { useTransactionFormHandlers } from "../hooks/useTransactionFormHandlers";
import { useTransactionMenuHandlers } from "../hooks/useTransactionMenuHandlers";
import { useTransactionSubmission } from "../hooks/useTransactionSubmission";
import { useCategoryDetails } from "../hooks/useCategoryDetails";
import { useRateAutoUpdate } from "../hooks/useRateAutoUpdate";
import { useFormValidation } from "../hooks/useFormValidation";
import type { FormData, TransferFormData } from "../hooks/useTransactionMenuHandlers";
import { useWorkspaceSettings } from "../../WorkspaceSettings/hooks/useWorkspaceSettings";

const initialFormData: FormData = {
  title: "",
  category: "",
  amount: "",
  currency: "",
  rate: "",
  defaultCurrencyRate: "",
  date: new Date().toISOString().split("T")[0],
  time: "",
  accountId: "",
  accountGroup: "",
};

const initialTransferFormData: TransferFormData = {
  fromAccountId: "",
  toAccountId: "",
  amount: "",
  date: new Date().toISOString().split("T")[0],
  time: "",
  transferRate: "",
};

export interface TransactionEditDialogStandaloneProps {
  workspaceId: string;
  workspaceOwnerId: string | null;
  accounts: Account[];
  expenseCategories: TransactionCategory[];
  incomeCategories: TransactionCategory[];
  categoryGroups: TransactionCategoryGroup[];
  currencies: Currency[];
  defaultCurrency: string;
  transactions: TransactionWithDate[];
  transactionCustomFields?: TransactionCustomField[];
  open: boolean;
  transactionToEdit: TransactionWithDate | null;
  onClose: () => void;
  onSaved?: () => void;
}

export function TransactionEditDialogStandalone({
  workspaceId,
  workspaceOwnerId,
  accounts,
  expenseCategories,
  incomeCategories,
  categoryGroups,
  currencies,
  defaultCurrency,
  transactions,
  transactionCustomFields = [],
  open,
  transactionToEdit,
  onClose,
  onSaved,
}: TransactionEditDialogStandaloneProps): ReactElement {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<"expense" | "income">("expense");
  const [editingTransaction, setEditingTransaction] = useState<TransactionWithDate | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [transferFormData, setTransferFormData] = useState<TransferFormData>(initialTransferFormData);
  const [showTime, setShowTime] = useState(false);
  const [showTransferTime, setShowTransferTime] = useState(false);
  const [showTransferFee, setShowTransferFee] = useState(false);
  const [currencyMenuAnchorEl, setCurrencyMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [uploadingTransferAttachment, setUploadingTransferAttachment] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingTransfer, setIsSubmittingTransfer] = useState(false);
  const [initialFormDataState, setInitialFormData] = useState<FormData | null>(null);
  const [initialTransferFormDataState, setInitialTransferFormData] = useState<TransferFormData | null>(null);
  const [rateManuallyEdited, setRateManuallyEdited] = useState(false);
  const [rateFromTransaction, setRateFromTransaction] = useState(false);
  const [defaultCurrencyRateManuallyEdited, setDefaultCurrencyRateManuallyEdited] = useState(false);
  const [transferRateManuallyEdited, setTransferRateManuallyEdited] = useState(false);
  const [selectedCategoryDetails, setSelectedCategoryDetails] = useState<any[]>([]);
  const [newlyAddedDetailOptions, setNewlyAddedDetailOptions] = useState<Record<string, string[]>>({});
  const [detailInputValues, setDetailInputValues] = useState<Record<string, string>>({});
  const [openDetailDropdowns, setOpenDetailDropdowns] = useState<Set<string>>(new Set());
  const [transactionHasHistory, setTransactionHasHistory] = useState<Set<string>>(new Set());
  const [snackbarElement, setSnackbarElement] = useState<{ severity: string; message: string } | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  /** False until re-baseline has run so Update button stays disabled from first paint (like Activity edit). */
  const [rebaselineDone, setRebaselineDone] = useState(false);

  const formDataRef = useRef(formData);
  const transferFormDataRef = useRef(transferFormData);
  formDataRef.current = formData;
  transferFormDataRef.current = transferFormData;

  const getAccountById = useAccountHelpers(accounts).getAccountById;
  const getAccountName = useAccountHelpers(accounts).getAccountName;
  const { calculateTransferRate } = useCurrencyHelpers(currencies, defaultCurrency);
  const { allowExpenseFee, allowTransferFee, timeFormat, dateFormat } = useWorkspaceSettings({ workspaceId });

  const noop = useCallback(() => {}, []);

  const { handleEdit } = useTransactionMenuHandlers({
    selectedTransaction: open ? transactionToEdit : null,
    workspaceId,
    workspaceSlug: undefined,
    isMobile: false,
    navigate: noop,
    accounts,
    defaultCurrency,
    expenseCategories,
    incomeCategories,
    categoryGroups,
    transactions,
    getAccountById,
    setEditingTransaction,
    setDialogOpen,
    setTransferDialogOpen,
    setDialogType,
    setFormData,
    setInitialFormData,
    setTransferFormData,
    setInitialTransferFormData,
    setShowTime,
    setShowTransferTime,
    setShowTransferFee,
    setSelectedCategoryDetails,
    setRateFromTransaction,
    setRateManuallyEdited,
    setDefaultCurrencyRateManuallyEdited,
    calculateTransferRate,
    setMenuAnchor: noop,
    setDeleteConfirmOpen: noop,
    setSelectedTransaction: noop,
    setHistoryDialogOpen: noop,
    setSnackbarElement,
    setSnackbarOpen,
    transactionCustomFields,
    handleMenuClose: noop,
    fetchTransactionHistory: async () => {},
  });

  useEffect(() => {
    if (open && transactionToEdit) {
      handleEdit(transactionToEdit);
    }
  }, [open, transactionToEdit, handleEdit]);

  useEffect(() => {
    if (!open) {
      setDialogOpen(false);
      setTransferDialogOpen(false);
      setEditingTransaction(null);
      setRebaselineDone(false);
    }
  }, [open]);

  // After opening for edit, re-baseline initial form data so any programmatic updates
  // (useCategoryDetails, useRateAutoUpdate) are included; keep Update disabled until then
  useEffect(() => {
    if (!open || !transactionToEdit || !dialogOpen) return;
    setRebaselineDone(false);
    const isTransfer = transactionToEdit.type === "transfer";
    const timer = setTimeout(() => {
      if (isTransfer) {
        setInitialTransferFormData((prev) => (prev ? { ...transferFormDataRef.current } : null));
      } else {
        setInitialFormData((prev) => (prev ? { ...formDataRef.current } : null));
      }
      setRebaselineDone(true);
    }, 150);
    return () => clearTimeout(timer);
  }, [open, transactionToEdit?.id, dialogOpen]);

  const { handleDialogClose, handleTransferDialogClose } = useDialogHandlers({
    defaultCurrency,
    setDialogOpen,
    setTransferDialogOpen,
    setEditingTransaction,
    setInitialFormData,
    setInitialTransferFormData,
    setIsSubmitting,
    setIsSubmittingTransfer,
    setCurrencyMenuAnchorEl,
    setFormData,
    setTransferFormData,
    setShowTime,
    setShowTransferTime,
    setRateFromTransaction,
    setRateManuallyEdited,
    setTransferRateManuallyEdited,
    setNewlyAddedDetailOptions,
    setDetailInputValues,
  });

  const {
    handleAccountChange,
    handleRefreshRate,
    handleRefreshDefaultCurrencyRate,
    handleRefreshTransferRate,
    getConvertedAmount,
  } = useTransactionFormHandlers({
    formData,
    transferFormData,
    defaultCurrency,
    accounts,
    isMobile: false,
    workspaceSlug: undefined,
    setFormData,
    setTransferFormData,
    setEditingTransaction,
    setInitialFormData,
    setInitialTransferFormData,
    setDialogOpen,
    setTransferDialogOpen,
    setDialogType,
    setShowTime,
    setShowTransferTime,
    setShowTransferFee,
    setRateFromTransaction,
    setRateManuallyEdited,
    setDefaultCurrencyRateManuallyEdited,
    setTransferRateManuallyEdited,
    getAccountById,
    calculateTransferRate,
  });

  const { handleSubmit, handleTransferSubmit } = useTransactionSubmission({
    workspaceId,
    workspaceOwnerId,
    formData,
    transferFormData,
    editingTransaction,
    dialogType,
    defaultCurrency,
    accounts,
    expenseCategories,
    incomeCategories,
    categoryGroups,
    newlyAddedDetailOptions,
    isSubmitting,
    isSubmittingTransfer,
    setIsSubmitting,
    setIsSubmittingTransfer,
    setDialogOpen,
    setTransferDialogOpen,
    setEditingTransaction,
    setFormData,
    setTransferFormData,
    setShowTime,
    setShowTransferTime,
    setShowTransferFee,
    setUploadingAttachment,
    setUploadingTransferAttachment,
    setSnackbarElement,
    setSnackbarOpen,
    setNewlyAddedDetailOptions,
    setDetailInputValues,
    setTransactionHasHistory: (updater) => setTransactionHasHistory(updater),
    getAccountById,
    getAccountName,
    calculateTransferRate,
    defaultCurrencyRateManuallyEdited,
    transactionCustomFields,
    allowExpenseFee,
    allowTransferFee,
  });

  const { deriveCategoryDetails, handleCreateDetailOption } = useCategoryDetails({
    dialogType,
    dialogOpen,
    transferDialogOpen,
    formData,
    editingTransaction,
    expenseCategories,
    incomeCategories,
    categoryGroups,
    transactions,
    selectedCategoryDetails,
    newlyAddedDetailOptions,
    detailInputValues,
    setSelectedCategoryDetails,
    setFormData,
    setDetailInputValues,
    setNewlyAddedDetailOptions,
    setOpenDetailDropdowns,
  });

  useRateAutoUpdate({
    formData,
    transferFormData,
    accounts,
    defaultCurrency,
    editingTransaction,
    initialFormData: initialFormDataState,
    initialTransferFormData: initialTransferFormDataState,
    rateFromTransaction,
    rateManuallyEdited,
    defaultCurrencyRateManuallyEdited,
    calculateTransferRate,
    setFormData,
    setTransferFormData,
    setInitialFormData,
    setInitialTransferFormData,
    setRateFromTransaction,
    setRateManuallyEdited,
    setDefaultCurrencyRateManuallyEdited,
  });

  const { isTransactionFormValid, hasTransactionChanges: rawHasTransactionChanges, isTransferFormValid, hasTransferChanges: rawHasTransferChanges } = useFormValidation({
    formData,
    transferFormData,
    initialFormData: initialFormDataState,
    initialTransferFormData: initialTransferFormDataState,
    editingTransaction,
    defaultCurrency,
    accounts,
    currencies,
  });
  // Disabled from first paint until re-baseline has run (so Update is never briefly enabled on open)
  const hasTransactionChanges = useCallback(() => !rebaselineDone ? false : rawHasTransactionChanges(), [rebaselineDone, rawHasTransactionChanges]);
  const hasTransferChanges = useCallback(() => !rebaselineDone ? false : rawHasTransferChanges(), [rebaselineDone, rawHasTransferChanges]);

  const handleClose = useCallback(() => {
    handleDialogClose();
    onClose();
    onSaved?.();
  }, [handleDialogClose, onClose, onSaved]);

  const handleTransferClose = useCallback(() => {
    handleTransferDialogClose();
    onClose();
    onSaved?.();
  }, [handleTransferDialogClose, onClose, onSaved]);

  if (!open) return <></>;

  return (
    <TransactionDialog
      workspaceId={workspaceId}
      dialogOpen={dialogOpen}
      transferDialogOpen={transferDialogOpen}
      dialogType={dialogType}
      editingTransaction={editingTransaction}
      isSubmitting={isSubmitting}
      isSubmittingTransfer={isSubmittingTransfer}
      formData={formData}
      transferFormData={transferFormData}
      showTime={showTime}
      showTransferTime={showTransferTime}
      currencyMenuAnchorEl={currencyMenuAnchorEl}
      uploadingAttachment={uploadingAttachment}
      uploadingTransferAttachment={uploadingTransferAttachment}
      selectedCategoryDetails={selectedCategoryDetails}
      newlyAddedDetailOptions={newlyAddedDetailOptions}
      detailInputValues={detailInputValues}
      openDetailDropdowns={openDetailDropdowns}
      rateManuallyEdited={rateManuallyEdited}
      defaultCurrencyRateManuallyEdited={defaultCurrencyRateManuallyEdited}
      transferRateManuallyEdited={transferRateManuallyEdited}
      accounts={accounts}
      currencies={currencies}
      defaultCurrency={defaultCurrency}
      expenseCategories={expenseCategories}
      incomeCategories={incomeCategories}
      categoryGroups={categoryGroups}
      allowExpenseFee={allowExpenseFee}
      allowTransferFee={allowTransferFee}
      setDialogOpen={setDialogOpen}
      setTransferDialogOpen={setTransferDialogOpen}
      setDialogType={setDialogType}
      setEditingTransaction={setEditingTransaction}
      setIsSubmitting={setIsSubmitting}
      setIsSubmittingTransfer={setIsSubmittingTransfer}
      setFormData={setFormData}
      setTransferFormData={setTransferFormData}
      setShowTime={setShowTime}
      setShowTransferTime={setShowTransferTime}
      setShowTransferFee={setShowTransferFee}
      setCurrencyMenuAnchorEl={setCurrencyMenuAnchorEl}
      setUploadingAttachment={setUploadingAttachment}
      setUploadingTransferAttachment={setUploadingTransferAttachment}
      setSelectedCategoryDetails={setSelectedCategoryDetails}
      setNewlyAddedDetailOptions={setNewlyAddedDetailOptions}
      setDetailInputValues={setDetailInputValues}
      setOpenDetailDropdowns={setOpenDetailDropdowns}
      setRateManuallyEdited={setRateManuallyEdited}
      setDefaultCurrencyRateManuallyEdited={setDefaultCurrencyRateManuallyEdited}
      setTransferRateManuallyEdited={setTransferRateManuallyEdited}
      handleSubmit={handleSubmit}
      handleTransferSubmit={handleTransferSubmit}
      handleDialogClose={handleClose}
      handleTransferDialogClose={handleTransferClose}
      handleAccountChange={handleAccountChange}
      handleRefreshRate={handleRefreshRate}
      handleRefreshDefaultCurrencyRate={handleRefreshDefaultCurrencyRate}
      handleRefreshTransferRate={handleRefreshTransferRate}
      handleCreateDetailOption={handleCreateDetailOption}
      deriveCategoryDetails={deriveCategoryDetails}
      calculateTransferRate={calculateTransferRate}
      getConvertedAmount={getConvertedAmount}
      isTransactionFormValid={isTransactionFormValid}
      hasTransactionChanges={hasTransactionChanges}
      isTransferFormValid={isTransferFormValid}
      hasTransferChanges={hasTransferChanges}
      timeFormat={timeFormat}
      dateFormat={dateFormat}
    />
  );
}

export default TransactionEditDialogStandalone;
